import { Analytics } from '@analytics';
import BrandingIcon from '@assets/images/sub_menu/Branding.svg';
import CommissionIcon from '@assets/images/sub_menu/Commissions_Navy.svg';
import CustomLinkIcon from '@assets/images/sub_menu/CustomLink.svg';
import SocialIcon from '@assets/images/sub_menu/Social.svg';
import ToursIcon from '@assets/images/sub_menu/Tours.svg';
import UpsellIcon from '@assets/images/sub_menu/Upsell.svg';
import LightningIcon from '@assets/images/tip_direct/LightningBolt.png';
import WaveIcon from '@assets/images/tip_direct_nav_menu/Wave.svg';
import ShareCompanyInviteButton from '@components/ShareCompanyInviteButton';
import InviteGuideModal from '@components/TapToTip/InviteGuideModal';
import ProPlanModal from '@components/TapToTip/ProPlanModal';
import { StyleBreakpoints, SubMenuTabs, UserRoles } from '@constants';
import { AppBar, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useIsFeatureForCompanyEnabled } from '@utils/hooks';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import { useInjectSaga } from '../../../../utils/injectSaga';
import JumpMenu from '../JumpMenu';
import SubMenu from '../SubMenu';
import { NavSurface, TapToTipTabs, formatTabLabel, gateFor, getDesktopMenu } from '../tipDirectTabs';
import { sendRecommendToAFriend } from './actions';
import messages from './messages';
import saga from './saga';

const useStyles = makeStyles(theme => ({
  tapToTipMenu: {
    display: 'none',
    flexDirection: 'row',
    height: '89px',
    position: 'relative',
    marginTop: '-20px',
    justifyContent: 'center',
    paddingBottom: '20px',
    overflowX: 'hidden',
    zIndex: '1',

    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      display: 'flex',
      flexDirection: 'column',
      // Trimmed from 262px to give the content + live-preview columns more room
      // on constrained desktops (e.g. 1080p at 150% OS scaling). The item indents
      // below were tightened to match so labels stay balanced in the narrower rail.
      width: '216px',
      justifyContent: 'flex-start',
      minHeight: '100vh',
      paddingTop: '14px',
      boxShadow: 'none',
      marginLeft: '0',
      paddingRight: '0 !important',
      position: 'sticky',
      overflowY: 'auto'
    },

    '&::-webkit-scrollbar': {
      width: '8px',
      height: '0px',
      paddingRight: '6px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#e7e7e7',
      width: '8px',
      borderRadius: '4px'
    }
  },
  tapToTipLink: {
    fontSize: '14px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '0',
    margin: 'auto 15px',
    height: '32px',
    textWrap: 'nowrap',

    // Card 383 — rows were 76.5px, so an admin's menu ran to ~1,150px and
    // scrolled on every laptop, and "Product Improvement" (161px at 14px) ran
    // past the 216px rail. At 36px and 13px the whole menu fits a 768px-tall
    // window and the longest label (149px) fits the 152px the row leaves it.
    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      display: 'flex',
      margin: '0',
      backgroundColor: 'transparent',
      justifyContent: 'flex-start',
      height: '36px',
      fontSize: '13px',
      border: 'none',
      textAlign: 'left',
      width: '100%',
      zIndex: '5'
    },
    '&.MuiButtonBase-root': {
      padding: '0'
    },
    '&:disabled': {
      opacity: '.44'
    },
    '&.MuiButton-root:hover': {
      backgroundColor: 'transparent'
    }
  },
  linkContainer: {
    position: 'relative'
  },
  activeTapToTipLink: {},
  adminLink: {
    display: 'none',
    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      display: 'flex'
    }
  }
}));

// Card 354 — one enum for both menus, defined with the tabs themselves.
export { TapToTipTabs };


// One rail row. The pill sits 8px in from each side and 2px in from top and
// bottom of the 36px row; the label truncates rather than wrapping, so a
// longer translation still keeps the row one line (its full text is the
// title).
function RailButton({ className, active, icon, label, onClick, badge }) {
  return (
    <Button
      className={`${className} group ${
        active ? '!text-indigo-700' : '!text-slate-600 hover:!text-slate-800'
      }`}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-controls="simple-menu"
      aria-haspopup="true">
      <img src={icon} className="w-5 shrink-0 ml-[18px]" alt="" />
      <div className="ml-2.5 pr-2 min-w-0 truncate" title={label} data-rail-label>
        {label}
      </div>
      <div
        className={`absolute inset-x-2 inset-y-0.5 rounded-lg -z-[1] transition-colors duration-150 ${
          active ? 'bg-indigo-50' : 'bg-transparent group-hover:bg-slate-100'
        }`}
      />
      {badge}
      <div className="absolute inset-0 -z-[2] bg-slate-50" />
    </Button>
  );
}

export function TipDirectLeftMenu({
  user,
  selectedTab,
  handleSendInvitesToGuides,
  exactTab,
  hideSubMenu = false,
  fillHeight = false
}) {
  useInjectSaga({ key: 'leftMenu', saga });
  const history = useHistory();
  const classes = useStyles();
  const intl = useIntl();
  const { tab } = useParams();
  const location = useLocation();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const isFreePlanEnabled = user.companyDetails.pricingSubscription.companyPlan === 0;

  const [shownTab, setShownTab] = useState(
    tab || selectedTab || location.state?.tab || TapToTipTabs.DASHBOARD
  );
  const [isRecommendModalOpen, setIsRecommendModalOpen] = useState(false);

  const isFeatureForCompanyEnabled = useIsFeatureForCompanyEnabled();

  useEffect(() => {
    if (location.state?.tab && shownTab !== location.state.tab) {
      setShownTab(location.state.tab);
    }
  }, [location.state, shownTab]);

  const handleTabClicked = ({ name, path }) => {
    setShownTab(name);
    history.push(path);
  };

  const handleUpgradeClick = () => {
    setIsUpgradeModalOpen(true);
  };

  // 1. system admin
  //  1.1 free plan - show upgrade button
  //  1.2 paid plan - show recommend button
  // 2. guide
  //  2.1 free plan - show recommend button
  //  2.2 paid plan - show recommend button
  const handleShowSideButtons = () => {
    if (user.role === UserRoles.paymentsAnalyst) {
      return null;
    }
    if (isFreePlanEnabled && user.role === UserRoles.admin) {
      return (
        <Button
          className="!mx-3 !my-1.5 !h-11 w-[calc(100%-24px)] !rounded-xl !bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !normal-case !shadow-sm transition-colors"
          onClick={handleUpgradeClick}>
          <img
            src={LightningIcon}
            className="w-3.5 mr-2 brightness-0 invert"
            alt={`Lightning Icon`}
          />
          {intl.formatMessage(messages.upgradeButton)}
        </Button>
      );
    }

    return (
      <>
        <ShareCompanyInviteButton
          companyName={user.companyName}
          inviteCode={user.inviteCode}
          compact
        />
        <Button
          className="!mx-3 !my-1.5 !h-11 w-[calc(100%-24px)] !rounded-xl !bg-white hover:!bg-slate-50 !border !border-solid !border-slate-300 !p-0 !text-slate-700 !normal-case !shadow-sm transition-colors !justify-start"
          onClick={() => setIsRecommendModalOpen(true)}>
          <img src={WaveIcon} className="w-[22px] ml-[23px]" alt={`Wave Icon`} />
          <div className="ml-8 flex flex-col text-[13px] text-left">
            <span className="font-semibold">{intl.formatMessage(messages.recommend)}</span>
            <span className="text-[10px] font-semibold text-slate-400 -mt-1">
              {intl.formatMessage(messages.usToAFriend)}
            </span>
          </div>
        </Button>
      </>
    );
  };

  // Card 354 — the tabs, their gates and their order come from the shared
  // definition the mobile menu also renders (../tipDirectTabs).
  const desktopMenu = getDesktopMenu(user);
  const settingsTab = desktopMenu.footer.find(({ name }) => name === TapToTipTabs.SETTINGS);
  // Card 362 — this block used to test `shownTab === name` with no `name` in
  // scope (it resolved to window.name), so Settings never showed as active.
  const settingsActive = shownTab === TapToTipTabs.SETTINGS;

  const subMenuTabsInfo = {
    Setup: [
      {
        name: SubMenuTabs.Setup.TOURS,
        label: intl.formatMessage(messages.tours),
        icon: ToursIcon
      },
      {
        name: SubMenuTabs.Setup.UPSELL,
        label: intl.formatMessage(messages.upsell),
        icon: UpsellIcon
      },
      {
        name: SubMenuTabs.Setup.SOCIAL,
        label: intl.formatMessage(messages.social),
        icon: SocialIcon
      },
      {
        name: SubMenuTabs.Setup.BRANDING,
        label: intl.formatMessage(messages.branding),
        icon: BrandingIcon
      },
      {
        name: SubMenuTabs.Setup.CUSTOM_LINK,
        label: intl.formatMessage(messages.customLink),
        icon: CustomLinkIcon
      },
      isFeatureForCompanyEnabled('PaymentLinks') && {
        name: SubMenuTabs.Setup.COMMISSION,
        label: intl.formatMessage(messages.commission),
        icon: CommissionIcon,
        isBetaFeature: true
      }
    ].filter(Boolean)
  };

  return (
    <>
      {/* fillHeight: a page that does not scroll itself (the PIP frame, card
          383) gives the rail a fixed-height row. The rail then fills it and
          scrolls inside, instead of being 100vh tall and pushed 20px up. */}
      <AppBar
        className={`${classes.tapToTipMenu} !bg-slate-50 !shadow-none border-0 border-r border-solid border-slate-200 ${
          fillHeight ? '!mt-0 !min-h-0 !h-full' : ''
        }`}>
        {desktopMenu.main.map(entry => {
          const { name, icons, requiredFeature } = entry;
          const { role } = gateFor(entry, NavSurface.DESKTOP);
          const staticIcon = icons.desktop.static;
          const activeIcon = icons.desktop.active;

          const active = shownTab === name;

          return (
            <div className={classes.linkContainer} key={name}>
              <RailButton
                className={`${classes.tapToTipLink} ${active &&
                  classes.activeTapToTipLink} ${role === UserRoles.admin && classes.adminLink}`}
                active={active}
                icon={active ? activeIcon : staticIcon}
                label={formatTabLabel(intl, entry, user, NavSurface.DESKTOP)}
                onClick={() => {
                  Analytics.track(`TTT menu clicked`, { page: name });
                  handleTabClicked(entry);
                }}
                badge={
                  requiredFeature &&
                  !isFeatureForCompanyEnabled(requiredFeature) && (
                    <div className="absolute right-4 inline-flex items-center bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded-md text-[10px] font-bold border border-solid border-indigo-200/55">
                      Pro
                    </div>
                  )
                }
              />
            </div>
          );
        })}
        {handleShowSideButtons()}

        {settingsTab && (
          <div className={classes.linkContainer}>
            <RailButton
              className={`${classes.tapToTipLink} ${settingsActive && classes.activeTapToTipLink}`}
              active={settingsActive}
              icon={
                settingsActive ? settingsTab.icons.desktop.active : settingsTab.icons.desktop.static
              }
              label={intl.formatMessage(messages.settings)}
              onClick={() => {
                handleTabClicked(settingsTab);
                Analytics.track(`TTT menu clicked`, { page: TapToTipTabs.SETTINGS });
              }}
            />
          </div>
        )}

        <InviteGuideModal
          isOpen={isRecommendModalOpen}
          setIsOpen={setIsRecommendModalOpen}
          sendInvitesToGuides={handleSendInvitesToGuides}
          companyName={user.companyName}
          isRecommendationEmail
          inviteCode={user.inviteCode}
        />

        <ProPlanModal isOpen={isUpgradeModalOpen} onClose={() => setIsUpgradeModalOpen(false)} />
      </AppBar>
      {!hideSubMenu && (
        <>
          <SubMenu tabsInfo={subMenuTabsInfo[shownTab]} exactTab={exactTab} />
          <JumpMenu tabsInfo={subMenuTabsInfo[shownTab]} />
        </>
      )}
    </>
  );
}

const mapStateToProps = state => ({
  user: state.user?.user
});

const mapDispatchToProps = dispatch => ({
  handleSendInvitesToGuides: payload => dispatch(sendRecommendToAFriend(payload))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TipDirectLeftMenu);
